import { useEffect, useRef, useState } from 'react';
import { getPipelineJobStatus } from '../../../services/pipeline.service';

/**
 * useStageJob
 *
 * Polls a pipeline job until it completes or fails.
 *
 * Returns:
 *   { polling, error, startPolling(jobId) }
 *
 * onResult(result) is called with the job's result payload when done.
 */
function useStageJob(onResult, onError, onActivity) {
  const [polling, setPolling] = useState(false);
  const [error, setError]     = useState(null);
  const timerRef              = useRef(null);
  const aliveRef              = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      clearInterval(timerRef.current);
    };
  }, []);

  const startPolling = (jobId) => {
    setPolling(true);
    setError(null);
    clearInterval(timerRef.current);

    const poll = async () => {
      try {
        const job = await getPipelineJobStatus(jobId);
        if (!aliveRef.current) return;

        if (job.status === 'completed') {
          clearInterval(timerRef.current);
          setPolling(false);
          // job.result is the agentcore wrapper: { success, stage, result: <LLM data>, agentActivity, session_id }
          // Unwrap one level so every stage handler receives the clean LLM payload.
          const payload = job.result?.result ?? job.result;
          // Forward agent activity log if present
          if (onActivity && job.result?.agentActivity) {
            onActivity(job.result.agentActivity);
          }
          onResult(payload);
        } else if (job.status === 'failed') {
          clearInterval(timerRef.current);
          setPolling(false);
          const msg = job.error || 'Stage generation failed.';
          setError(msg);
          if (onError) onError(msg);
        }
        // still queued / processing — keep polling
      } catch (err) {
        // transient network error — keep polling
        console.warn('Pipeline poll error (will retry):', err.message);
      }
    };

    // First check after 10 s, then every 8 s
    const first = setTimeout(() => {
      poll();
      timerRef.current = setInterval(poll, 8000);
    }, 10000);

    // Store timeout so cleanup can cancel it
    timerRef.current = first;
  };

  return { polling, error, startPolling };
}

export default useStageJob;
